import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Trash2, Mail, FileText } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import './LegalPages.css';

const CONTACT_EMAIL = 'soporte@wannacolive.app';

function LegalLayout({ icon: Icon, title, subtitle, children }) {
  const { t } = useLanguage();
  return (
    <main className="legal-page min-h-screen-safe bg-background" data-testid="legal-page">
      <div className="legal-page__shell">
        <Link to="/" className="legal-page__back" aria-label={t('legalBack')} data-testid="legal-back-link">
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          <span className="sr-only">{t('legalBack')}</span>
        </Link>
        <header className="legal-page__header">
          <img src="/logo192.png" alt="WannaColive" className="legal-page__logo brand-logo-adaptive" />
          <div className="legal-page__heading">
            <div className="legal-page__section-label"><Icon className="w-5 h-5" aria-hidden="true" /><span>WannaColive</span></div>
            <h1 className="font-heading font-bold text-2xl text-foreground leading-tight">{title}</h1>
            <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
          </div>
        </header>
        <div className="legal-page__content text-foreground space-y-6">{children}</div>
        <p className="legal-page__footer text-xs text-muted-foreground">
          {t('legalLastUpdated')}: {t('legalUpdatedDate')} · <a href={`mailto:${CONTACT_EMAIL}`} className="underline hover:text-foreground">{CONTACT_EMAIL}</a>
        </p>
      </div>
    </main>
  );
}

function Section({ title, children }) {
  return <section><h2 className="font-heading font-bold text-base text-foreground mb-2">{title}</h2><div className="text-sm text-muted-foreground leading-relaxed space-y-2">{children}</div></section>;
}

function List({ items }) {
  const { t } = useLanguage();
  return <ul className="list-disc pl-5 space-y-1">{items.map((key) => <li key={key}>{t(key)}</li>)}</ul>;
}

export function PrivacyPage() {
  const { t } = useLanguage();
  return (
    <LegalLayout icon={ShieldCheck} title={t('legalPrivacyTitle')} subtitle={t('legalPrivacySubtitle')}>
      <Section title={t('legalPrivacyS1')}><p>{t('legalPrivacyWhoBefore')} <span className="text-foreground">wannacolive.app</span>{t('legalPrivacyWhoAfterSite')}{' '}<a href={`mailto:${CONTACT_EMAIL}`} className="underline text-foreground">{CONTACT_EMAIL}</a>{t('legalPrivacyWhoAfterEmail')}</p></Section>
      <Section title={t('legalPrivacyS2')}>
        <p className="text-foreground font-semibold">{t('legalPrivacyAccountData')}</p>
        <List items={['legalPrivacyAccount1', 'legalPrivacyAccount2', 'legalPrivacyAccount3', 'legalPrivacyAccount4', 'legalPrivacyAccount5']} />
        <p className="text-foreground font-semibold pt-2">{t('legalPrivacyGenerated')}</p>
        <List items={['legalPrivacyGenerated1', 'legalPrivacyGenerated2', 'legalPrivacyGenerated3', 'legalPrivacyGenerated4', 'legalPrivacyGenerated5']} />
        <p className="text-foreground font-semibold pt-2">{t('legalPrivacyDeviceData')}</p>
        <List items={['legalPrivacyDevice1', 'legalPrivacyDevice2', 'legalPrivacyDevice3']} />
        <p>{t('legalPrivacyNoCollect')}</p>
      </Section>
      <Section title={t('legalPrivacyS3')}><List items={['legalPrivacyUse1', 'legalPrivacyUse2', 'legalPrivacyUse3', 'legalPrivacyUse4']} /><p>{t('legalPrivacyNoSell')}</p></Section>
      <Section title={t('legalPrivacyS4')}><p>{t('legalPrivacyLegalBasis')}</p></Section>
      <Section title={t('legalPrivacyS5')}><p>{t('legalPrivacyShareIntro')}</p><List items={['legalPrivacyShare1', 'legalPrivacyShare2', 'legalPrivacyShare3', 'legalPrivacyShare4', 'legalPrivacyShare5']} /><p>{t('legalPrivacyShareAfter')}</p></Section>
      <Section title={t('legalPrivacyS6')}><p>{t('legalPrivacyRetention')}{' '}<Link to="/eliminar-cuenta" className="underline text-foreground">{t('legalPrivacyDeleteLink')}</Link>.</p></Section>
      <Section title={t('legalPrivacyS7')}><p>{t('legalPrivacyRightsBefore')}{' '}<a href={`mailto:${CONTACT_EMAIL}`} className="underline text-foreground">{CONTACT_EMAIL}</a>{' '}{t('legalPrivacyRightsAfter')}</p></Section>
      <Section title={t('legalPrivacyS8')}><p>{t('legalPrivacySecurity')}</p></Section>
      <Section title={t('legalPrivacyS9')}><p>{t('legalPrivacyMinors')}</p></Section>
      <Section title={t('legalPrivacyS10')}><p>{t('legalPrivacyCookies')}</p></Section>
      <Section title={t('legalPrivacyS11')}><p>{t('legalPrivacyChanges')}</p></Section>
    </LegalLayout>
  );
}

export function DeleteAccountPage() {
  const { t } = useLanguage();
  return (
    <LegalLayout icon={Trash2} title={t('legalDeleteTitle')} subtitle={t('legalDeleteSubtitle')}>
      <Section title={t('legalDeleteS1')}>
        <p>{t('legalDeleteAppIntro')}</p>
        <ol className="list-decimal pl-5 space-y-1">
          <li>{t('legalDeleteStep1')}</li>
          <li>{t('legalDeleteStep2Before')} <span className="text-foreground font-semibold">{t('legalDeleteStep2Label')}</span>.</li>
          <li>{t('legalDeleteStep3Before')} <span className="text-foreground font-semibold">{t('legalDeleteStep3Label')}</span>.</li>
          <li>{t('legalDeleteStep4Before')} <span className="text-foreground font-semibold">{t('legalDeleteStep4Label')}</span> {t('legalDeleteStep4After')}</li>
        </ol>
        <p>{t('legalDeleteAppAfter')}</p>
      </Section>
      <Section title={t('legalDeleteS2')}><p>{t('legalDeleteEmailBefore')}{' '}<a href={`mailto:${CONTACT_EMAIL}?subject=Eliminar%20mi%20cuenta`} className="underline text-foreground">{CONTACT_EMAIL}</a>{' '}{t('legalDeleteEmailAfter')} <span className="text-foreground font-semibold">{t('legalDeleteSubject')}</span>.{' '}{t('legalDeleteIdentity')}</p></Section>
      <Section title={t('legalDeleteS3')}><List items={['legalDeleteRemoved1', 'legalDeleteRemoved2', 'legalDeleteRemoved3', 'legalDeleteRemoved4', 'legalDeleteRemoved5', 'legalDeleteRemoved6']} /></Section>
      <Section title={t('legalDeleteS4')}><p>{t('legalDeleteKept')}{' '}<span className="text-foreground font-semibold">{t('legalDeleteAnonymized')}</span>{' '}{t('legalDeleteKeptAfter')}</p></Section>
      <Section title={t('legalDeleteImportant')}><List items={['legalDeleteWarning1', 'legalDeleteWarning2', 'legalDeleteWarning3']} /></Section>
      <div className="flex items-center gap-2 pt-2 border-t-2 border-foreground text-xs text-muted-foreground"><Mail className="w-4 h-4 flex-shrink-0" aria-hidden="true" /><a href={`mailto:${CONTACT_EMAIL}`} className="underline hover:text-foreground">{CONTACT_EMAIL}</a></div>
    </LegalLayout>
  );
}

export function TermsPage() {
  const { t } = useLanguage();
  return (
    <LegalLayout icon={FileText} title={t('legalTermsTitle')} subtitle={t('legalTermsSubtitle')}>
      <Section title={t('legalTermsS1')}><p>{t('legalTermsP1')}</p><p>{t('legalTermsP2')}</p></Section>
      <Section title={t('legalTermsS2')}><List items={['legalTermsAccount1', 'legalTermsAccount2', 'legalTermsAccount3', 'legalTermsAccount4']} /></Section>
      <Section title={t('legalTermsS3')}><p>{t('legalTermsAcceptIntro')}</p><List items={['legalTermsAccept1', 'legalTermsAccept2', 'legalTermsAccept3', 'legalTermsAccept4', 'legalTermsAccept5']} /><p>{t('legalTermsAcceptAfter')}</p></Section>
      <Section title={t('legalTermsS4')}><p>{t('legalTermsContent')}</p></Section>
      <Section title={t('legalTermsS5')}><List items={['legalTermsClaims1', 'legalTermsClaims2', 'legalTermsClaims3']} /></Section>
      <Section title={t('legalTermsS6')}><p>{t('legalTermsAvailabilityBefore')}{' '}<a href={`mailto:${CONTACT_EMAIL}`} className="underline text-foreground">{CONTACT_EMAIL}</a>{t('legalTermsAvailabilityAfter')}</p></Section>
      <Section title={t('legalTermsS7')}><p>{t('legalTermsNotifications')}</p></Section>
      <Section title={t('legalTermsS8')}><p>{t('legalTermsIP')}</p></Section>
      <Section title={t('legalTermsS9')}><p>{t('legalTermsChanges')}</p></Section>
      <Section title={t('legalTermsS10')}><p>{t('legalTermsLaw')}</p></Section>
      <Section title={t('legalTermsS11')}><p>{t('legalTermsContact')}{' '}<a href={`mailto:${CONTACT_EMAIL}`} className="underline text-foreground">{CONTACT_EMAIL}</a></p></Section>
    </LegalLayout>
  );
}

export default PrivacyPage;
